function isValidRect(rect) {
  return rect != null &&
    [rect.left, rect.right, rect.top, rect.bottom].every(Number.isFinite) &&
    rect.left < rect.right && rect.top < rect.bottom;
}

/**
 * Return the nearest safe upward offset from the unlifted base rectangle.
 * Only positive horizontal overlap matters. An already non-overlapping base
 * stays put, even when its clearance is smaller than gap.
 * After a collision, every relevant obstacle must have at least gap clearance.
 * A result of 0 can also mean no safe position; the caller decides that UI state.
 * Invalid rectangles are ignored (invalid base/options return 0). Top and gap
 * must be finite, nonnegative numbers. Inputs are never modified or remembered.
 */
export function chooseChatLift({ baseRect, obstacles, top = 8, gap = 8 }) {
  if (!isValidRect(baseRect) || !Array.isArray(obstacles) ||
      !Number.isFinite(top) || top < 0 || !Number.isFinite(gap) || gap < 0) return 0;
  const relevant = obstacles.filter((rect) =>
    isValidRect(rect) && baseRect.left < rect.right && baseRect.right > rect.left);
  if (!relevant.some((rect) => baseRect.top < rect.bottom && baseRect.bottom > rect.top)) return 0;
  const candidates = relevant.map((rect) => baseRect.bottom - rect.top + gap)
    .filter((lift) => lift > 0 && baseRect.top - lift >= top)
    .sort((a, b) => a - b);
  return candidates.find((lift) => relevant.every((rect) =>
    baseRect.bottom - lift <= rect.top - gap ||
    baseRect.top - lift >= rect.bottom + gap)) ?? 0;
}

const INSTANCE = Symbol.for('grindjoy.chatAvoidance');
const READY = Symbol.for('grindjoy.chatAvoidance.ready');
const FLAG = 'data-gjl-chat-avoidance';
const STOWED = 'data-gjl-chat-stowed';
const CART_MODE = 'data-gjl-chat-cart-mode';
const OFFSET = '--gjl-chat-avoidance-offset';
const NATIVE_OFFSET = '--shopify-chat-activator-offset';
// The product gallery is the PDP's primary purchase content on mobile: the
// launcher must never sit on product photos or their thumbnails. It counts as
// a purchase surface (may move/stow the launcher) and as a landing obstacle.
const GALLERY = 'main product-info media-gallery';
// Collection cards: the launcher must not cover a card's eyebrow, name, price,
// rating or swatches. It may land on the card photo instead.
const CARD_TEXT = '#product-grid .card__information';
// Only purchase surfaces may authorize moving or stowing the native launcher.
// Native Bundle controls live inside the add-to-cart form; variant/quantity
// groups can live outside it. Native cart summaries and checkout also qualify.
// Product titles/CTAs qualify, not image cards.
const PURCHASES = [
  '#checkout', '#main-cart-footer .cart__blocks',
  'main .product-form__submit', 'main form[data-type="add-to-cart-form"]',
  'main form[action*="/cart/add"]', 'main .product-form__input', 'main variant-selects',
  'main .shopify-payment-button', 'main shopify-accelerated-checkout',
  'main .gyj-prod-cta', 'main .gyj-product h3', 'main .product__title',
  'main .gjl-cro-sticky', 'main .gjl-cro-price', 'main .gjl-pdp-intro', 'main .gjl-delivery-estimate', '#product-grid .card__heading',
  CARD_TEXT, GALLERY
].join(',');
const TARGETS = [
  'main button', 'main input:not([type="hidden"])', 'main select', 'main textarea',
  'main label', 'main summary', 'main .product-form__input', 'main variant-selects',
  'main form[data-type="add-to-cart-form"]', 'main .shopify-payment-button',
  'main shopify-accelerated-checkout', 'main .gyj-prod-cta', 'main .gyj-product h3',
  'main .price', 'main .gyj-price', 'main .product__title', 'main .product__tax',
  'main .gjl-pdp-intro', 'main .gjl-cro-price', 'main .gjl-cro-sticky', 'main .jdgm-prev-badge', 'main .gjl-delivery-estimate', 'main .gjl-cro-trust',
  '#product-grid .card__heading', CARD_TEXT, 'header button', 'header a', 'header summary', '.header-wrapper', '.announcement-bar-section', GALLERY
].join(',');
const MODALS = '#bndlr-discount-message, #gyj-age-gate, #shopify-pc__banner, #shopify-pc__prefs__dialog, dialog[open], [aria-modal="true"], cart-drawer.active, header details[open]';
// Elements that can become MODALS by an attribute change. Only these are watched
// for attributes (never the whole body subtree).
const MODAL_CANDIDATES = '#bndlr-discount-message, #gyj-age-gate, #shopify-pc__banner, #shopify-pc__prefs__dialog, dialog, [aria-modal], cart-drawer, product-modal, modal-dialog, header details';
const STATE_ATTRIBUTES = ['style', 'class', 'hidden', 'open', 'aria-hidden', 'inert', 'aria-modal'];

function visible(element, allowTransparent = false) {
  if (!element || element.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
  const closed = element.closest('details:not([open])');
  if (closed && !closed.querySelector(':scope > summary')?.contains(element)) return false;
  // Style first: hidden overlays are rejected without forcing a layout.
  const style = getComputedStyle(element);
  if (style.display === 'none' || style.visibility === 'hidden' || (allowTransparent !== true && Number(style.opacity) === 0)) return false;
  const rect = element.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

function foregroundOverlay(element) {
  if (!visible(element)) return false;
  const rect = element.getBoundingClientRect();
  const view = window.visualViewport;
  const top = view?.offsetTop || 0;
  const left = view?.offsetLeft || 0;
  const bottom = top + (view?.height || innerHeight);
  const right = left + (view?.width || innerWidth);
  return rect.bottom > top && rect.top < bottom && rect.right > left && rect.left < right;
}

// One pending reaction per page, created outside any instance scope. Only the
// clearable listener may retain an active instance while the native chat loads.
function createChatReady() {
  const ready = {listener: null};
  customElements.whenDefined('shopify-chat').then(() => {
    const listener = ready.listener;
    ready.listener = null;
    listener?.();
  });
  return ready;
}

/*
 * Performance model: all work is coalesced into at most one update per frame,
 * and only runs while a native chat host exists. Candidate elements are
 * collected only after DOM insertions/removals in <main>, an
 * IntersectionObserver keeps the set that is on screen, and each update
 * measures only on-screen candidates in the launcher's column. Attribute
 * changes are observed on the chat host, <html>, <body> and modal candidates,
 * not across the whole document.
 */
function createAvoidance() {
  const lifetime = new AbortController();
  const media = matchMedia('(max-width: 749px)');
  let active = false;
  let destroyed = false;
  let pageSuspended = false;
  let frame = 0;
  let host = null;
  let container = null;
  let button = null;
  let baseOffset = 0;
  let appliedLift = 0;
  let pointerOnChat = false;
  let failedHost = null;
  let returnTarget = null;
  let returnHost = null;
  let observedRoots = new WeakSet();
  let elementsDirty = true;
  let tracked = new Set();
  let purchaseElements = [];
  let targetElements = [];
  let observedMain = null;
  // Last measured unlifted launcher slot: {rect, offset, width, height}.
  let slot = null;
  const inView = new Set();
  let soonTimer = 0;
  const resize = new ResizeObserver(soon);
  // Chat host attributes and its shadow DOM: re-find the launcher only after
  // structural changes there.
  const shadows = new MutationObserver(records => {
    if (records.some(record => record.type === 'childList')) button = null;
    if (records.some(record => record.target === host && record.attributeName === 'open')) schedule();
    else soon();
  });
  // Body direct children (chat host, app popups) and <main> insertions/removals.
  const structure = new MutationObserver(() => { elementsDirty = true; soon(); });
  // State attributes on <html>, <body> and modal candidates only.
  const states = new MutationObserver(schedule);
  const seen = new IntersectionObserver(entries => {
    for (const entry of entries) {
      if (entry.isIntersecting) inView.add(entry.target);
      else inView.delete(entry.target);
    }
    schedule();
  });

  function reset() {
    if (host) {
      host.removeAttribute(FLAG);
      host.removeAttribute(STOWED);
      host.style.removeProperty(OFFSET);
    }
    appliedLift = 0;
  }

  // Stow without un-stowing first: avoids a remove/add attribute pair (and the
  // style invalidation it causes) on every frame while stowed.
  function stow() {
    host.removeAttribute(FLAG);
    host.style.removeProperty(OFFSET);
    appliedLift = 0;
    if (!host.hasAttribute(STOWED)) host.setAttribute(STOWED, '');
  }

  function observeRoot(root) {
    if (!observedRoots.has(root)) {
      shadows.observe(root, {subtree: true, childList: true, attributes: true});
      observedRoots.add(root);
    }
  }

  function launcher(root, requireVisible = true) {
    if (!root) return null;
    observeRoot(root);
    for (const candidate of root.querySelectorAll('[part~="activator"]')) {
      // Native @starting-style fades from opacity 0 after display restoration.
      // Its laid-out container is still needed to decide whether a slot exists.
      if (!requireVisible || visible(candidate, true)) return candidate;
    }
    for (const element of root.querySelectorAll('*')) {
      if (element.shadowRoot) {
        const found = launcher(element.shadowRoot, requireVisible);
        if (found) return found;
      }
    }
    return null;
  }

  function observeStates() {
    states.disconnect();
    states.observe(document.documentElement, {attributes: true, attributeFilter: ['aria-hidden', 'inert', 'aria-modal', 'data-gyj-age']});
    states.observe(document.body, {attributes: true, attributeFilter: STATE_ATTRIBUTES});
    for (const element of document.querySelectorAll(MODAL_CANDIDATES)) {
      states.observe(element, {attributes: true, attributeFilter: STATE_ATTRIBUTES});
    }
  }

  // Rebuild candidate lists after DOM insertions/removals; no layout reads here.
  function collect() {
    elementsDirty = false;
    const main = document.querySelector('main');
    if (main !== observedMain) {
      structure.disconnect();
      structure.observe(document.body, {childList: true});
      if (main) {
        structure.observe(main, {childList: true, subtree: true});
        resize.observe(main);
      }
      observedMain = main;
    }
    const purchases = [...document.querySelectorAll(PURCHASES)]
      .filter(e => !e.matches('.gyj-product') && !(e.matches('a') && e.querySelector('img')));
    const links = [...document.querySelectorAll('main a')].filter(a => !a.matches('.gyj-product') && !a.querySelector('img'));
    const targets = new Set([...document.querySelectorAll(TARGETS), ...links, ...purchases]);
    for (const element of tracked) {
      if (!targets.has(element)) {
        seen.unobserve(element);
        inView.delete(element);
      }
    }
    for (const element of targets) {
      if (!tracked.has(element)) {
        seen.observe(element);
        // Measured this frame; the observer's first callback prunes it if off screen.
        inView.add(element);
      }
    }
    tracked = targets;
    purchaseElements = purchases;
    targetElements = [...targets];
    observeStates();
  }

  function forgetElements() {
    seen.disconnect();
    inView.clear();
    tracked = new Set();
    purchaseElements = [];
    targetElements = [];
    observedMain = null;
    elementsDirty = true;
  }

  function cartMode() {
    return document.body?.classList.contains('template-cart') &&
      !!document.querySelector('[data-gjl-chat-cart]') &&
      visible(document.querySelector('[data-gjl-chat-trigger]'));
  }

  function restoreInlineFocus() {
    const popup = document.querySelector('#bndlr-discount-message');
    const target = foregroundOverlay(popup) ? popup.querySelector('.bndlr-message-yes') :
      returnTarget?.isConnected ? returnTarget : document.querySelector('[data-gjl-chat-trigger]');
    const focused = document.activeElement;
    if (active && media.matches && target && visible(target) && (!focused || focused === host || focused === document.body || !focused.isConnected)) {
      target.focus({preventScroll: true});
    }
    returnTarget = null;
    returnHost = null;
  }

  function openFromInline(event) {
    if (!active || event.defaultPrevented || event.button > 0 || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey || !cartMode()) return;
    const trigger = event.target?.closest?.('[data-gjl-chat-trigger]');
    if (!trigger) return;
    if ([...document.querySelectorAll(MODALS)].some(element => foregroundOverlay(element))) {
      event.preventDefault();
      return;
    }
    const nativeHost = document.querySelector('shopify-chat');
    if (!nativeHost) return;
    try {
      if (!nativeHost.hasAttribute('open')) {
        const nativeButton = launcher(nativeHost.shadowRoot, false);
        if (!nativeButton) return;
        nativeButton.click();
        if (!nativeHost.hasAttribute('open')) return;
        returnTarget = trigger;
        returnHost = nativeHost;
      }
      event.preventDefault();
      schedule();
    } catch {}
  }

  // Per-frame for direct interaction (scroll, resize, focus, pointer, toggles).
  function schedule() {
    if (active && !frame) frame = requestAnimationFrame(update);
  }

  // Throttled for page churn (DOM insertions, resizes, theme reveals): during
  // load these fire nearly every frame and each update forces a layout read.
  function soon() {
    if (active && !soonTimer) soonTimer = setTimeout(() => { soonTimer = 0; schedule(); }, 120);
  }

  // Pure geometry for an unlifted launcher rectangle. Only on-screen candidates
  // in the launcher's column are measured (others can never block or obstruct a
  // slot), and computed style is read only after the cheap rectangle test.
  function decide(baseRect) {
    const view = window.visualViewport;
    const top = (view?.offsetTop || 0) + 8;
    const bottom = (view?.offsetTop || 0) + (view?.height || innerHeight);
    const inColumn = r => r.bottom > top && r.top < bottom && baseRect.left < r.right && baseRect.right > r.left;
    const blocked = purchaseElements.some(element => {
      if (!inView.has(element)) return false;
      const r = element.getBoundingClientRect();
      return inColumn(r) && baseRect.top < r.bottom && baseRect.bottom > r.top && visible(element);
    });
    if (!blocked) return {blocked, lift: 0, stow: false};
    // Ordinary content still excludes unsafe landing slots, but cannot trigger avoidance.
    const obstacles = [];
    for (const element of targetElements) {
      if (!inView.has(element)) continue;
      const r = element.getBoundingClientRect();
      if (inColumn(r) && visible(element)) obstacles.push(r);
    }
    const lift = chooseChatLift({baseRect, obstacles, top});
    return {blocked, lift, stow: lift === 0};
  }

  function update() {
    frame = 0;
    if (!active) return;
    try {
      const next = document.querySelector('shopify-chat');
      if (next !== host) {
        reset();
        host?.removeAttribute(CART_MODE);
        if (next !== returnHost) {
          returnTarget = null;
          returnHost = null;
        }
        shadows.disconnect();
        observedRoots = new WeakSet();
        if (container) resize.unobserve(container);
        host = next;
        container = null;
        button = null;
        slot = null;
        if (host) shadows.observe(host, {attributes: true, attributeFilter: ['open']});
        else forgetElements();
      }
      // Nothing to adapt until the native chat exists; body childList still notices it.
      if (!host) return;
      if (elementsDirty) collect();
      const onCart = cartMode();
      host.toggleAttribute(CART_MODE, onCart);
      if (returnTarget && returnHost === host && !host.hasAttribute('open')) restoreInlineFocus();
      if (host.hasAttribute('open')) {
        reset();
        return;
      }
      if ([...document.querySelectorAll(MODALS)].some(element => foregroundOverlay(element))) {
        stow();
        return;
      }
      if (onCart) {
        reset();
        return;
      }
      // While stowed (display: none), decide from the last measured launcher slot
      // instead of un-stowing to re-measure every frame: that write/read pair
      // forced a full style + layout pass per frame on the PDP while the gallery
      // was on screen. The cache is valid only for the same viewport and native offset.
      if (host.hasAttribute(STOWED) && slot && host !== failedHost && !pointerOnChat && document.activeElement !== host) {
        const nativeOffset = Number.parseFloat(getComputedStyle(host).getPropertyValue(NATIVE_OFFSET));
        const view = window.visualViewport;
        if (nativeOffset === slot.offset && innerWidth === slot.width && (view?.height || innerHeight) === slot.height &&
            decide(slot.rect).stow) return;
      }
      // Re-measure synchronously without painting an intermediate visible state.
      host.removeAttribute(STOWED);
      if (host === failedHost) return;
      // Never move the control during its own pointer or keyboard interaction.
      if (pointerOnChat || document.activeElement === host) return;
      if (!button?.isConnected || !visible(button, true)) button = launcher(host.shadowRoot);
      const nextContainer = button?.parentElement;
      if (!button || !nextContainer?.classList.contains('activator-nudge')) {
        reset();
        return;
      }
      if (nextContainer !== container) {
        reset();
        if (container) resize.unobserve(container);
        container = nextContainer;
        baseOffset = Number.parseFloat(getComputedStyle(host).getPropertyValue(NATIVE_OFFSET));
        resize.observe(container);
      }
      // The native offset can change when the product purchase bar appears.
      // Only refresh without our flag; with it the CSS exposes the lifted value.
      if (!host.hasAttribute(FLAG)) {
        baseOffset = Number.parseFloat(getComputedStyle(host).getPropertyValue(NATIVE_OFFSET));
      }
      if (!Number.isFinite(baseOffset)) return;
      const rect = container.getBoundingClientRect();
      const baseRect = {left: rect.left, right: rect.right, top: rect.top + appliedLift, bottom: rect.bottom + appliedLift};
      const view = window.visualViewport;
      slot = {rect: baseRect, offset: baseOffset, width: innerWidth, height: view?.height || innerHeight};
      const decision = decide(baseRect);
      if (!decision.blocked) {
        reset();
        return;
      }
      if (decision.stow) {
        // Owner-approved exception: no safe slot, no active chat interaction.
        stow();
        return;
      }
      const lift = decision.lift;
      if (Math.abs(lift - appliedLift) < 0.25) return;
      const expectedTop = baseRect.top - lift;
      if (lift === 0) reset();
      else {
        host.style.setProperty(OFFSET, `${baseOffset + lift}px`);
        host.setAttribute(FLAG, '');
        appliedLift = lift;
      }
      // Fail back to the native control if a vendor change breaks this adapter.
      if (Math.abs(container.getBoundingClientRect().top - expectedTop) > 1) {
        reset();
        failedHost = host;
      }
    } catch {
      reset();
      failedHost = host;
    }
  }

  function start() {
    if (destroyed || active || !media.matches || !document.body) return;
    active = true;
    elementsDirty = true;
    // Until the host exists, only body's direct children are watched (the chat host is one).
    structure.observe(document.body, {childList: true});
    resize.observe(document.documentElement);
    if (!customElements.get('shopify-chat')) {
      const ready = window[READY] ||= createChatReady();
      ready.listener = schedule;
    }
    schedule();
  }

  function stop() {
    active = false;
    if (window[READY]?.listener === schedule) window[READY].listener = null;
    cancelAnimationFrame(frame);
    frame = 0;
    clearTimeout(soonTimer);
    soonTimer = 0;
    structure.disconnect();
    states.disconnect();
    shadows.disconnect();
    resize.disconnect();
    forgetElements();
    observedRoots = new WeakSet();
    reset();
    host?.removeAttribute(CART_MODE);
    host = null;
    container = null;
    button = null;
    slot = null;
    pointerOnChat = false;
    returnTarget = null;
    returnHost = null;
  }

  function sync() { if (media.matches && !document.hidden && !pageSuspended) start(); else stop(); }
  const options = {signal: lifetime.signal, passive: true};
  // Scroll/resize are coalesced to one update per frame by schedule().
  window.addEventListener('scroll', schedule, options);
  window.addEventListener('resize', schedule, options);
  window.visualViewport?.addEventListener('resize', schedule, options);
  window.visualViewport?.addEventListener('scroll', schedule, options);
  // Theme reveals move content with transforms (no ResizeObserver signal):
  // re-measure when they start and once more when they settle.
  document.addEventListener('animationstart', soon, options);
  document.addEventListener('animationend', soon, options);
  document.addEventListener('animationcancel', soon, options);
  document.addEventListener('transitionrun', soon, options);
  document.addEventListener('transitionend', soon, options);
  document.addEventListener('transitioncancel', soon, options);
  // <details>/<dialog> open state (header menus, drawers) without attribute observers.
  document.addEventListener('toggle', schedule, {signal: lifetime.signal, passive: true, capture: true});
  document.addEventListener('change', schedule, options);
  document.addEventListener('click', schedule, options);
  document.addEventListener('click', openFromInline, {signal: lifetime.signal});
  document.addEventListener('focusin', schedule, options);
  document.addEventListener('focusout', schedule, options);
  document.addEventListener('pointerdown', event => { pointerOnChat = !!host && event.composedPath().includes(host); }, options);
  const pointerEnd = () => { pointerOnChat = false; schedule(); };
  document.addEventListener('pointerup', pointerEnd, options);
  document.addEventListener('pointercancel', pointerEnd, options);
  const sectionChange = () => { elementsDirty = true; schedule(); };
  document.addEventListener('shopify:section:load', sectionChange, options);
  document.addEventListener('shopify:section:unload', sectionChange, options);
  document.addEventListener('visibilitychange', sync, {signal: lifetime.signal});
  media.addEventListener('change', sync, {signal: lifetime.signal});
  // Visibility alone must not restart a page suspended for navigation/BFCache.
  window.addEventListener('pagehide', () => { pageSuspended = true; stop(); }, {signal: lifetime.signal});
  window.addEventListener('pageshow', () => { pageSuspended = false; sync(); }, {signal: lifetime.signal});
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', sync, {once: true, signal: lifetime.signal});
  else sync();
  return {destroy() { destroyed = true; stop(); lifetime.abort(); }};
}

if (typeof window !== 'undefined' && window.ResizeObserver && window.MutationObserver && window.IntersectionObserver && window.AbortController && window.customElements) {
  window[INSTANCE]?.destroy();
  window[INSTANCE] = createAvoidance();
}
